import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import '../styles/ControleDiarias.css';
import { apiService } from '../services/ApiService';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';

const ControleDiarias = () => {
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [pedreiros, setPedreiros] = useState([]);
  const [diarias, setDiarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberta, setModalAberta] = useState(false);
  const [modalTipo, setModalTipo] = useState('pedreiro'); // 'pedreiro' ou 'diaria'
  const [editandoId, setEditandoId] = useState(null);
  
  // Formulário para adicionar/editar pedreiro
  const [formPedreiro, setFormPedreiro] = useState({
    nome: '',
    valorDiaria: '',
    telefone: '',
    observacoes: ''
  });
  
  // Formulário para adicionar diária
  const [formDiaria, setFormDiaria] = useState({
    pedreiroId: '',
    data: new Date().toISOString().substring(0, 10),
    quantidade: 1,
    valorUnitario: '',
    observacoes: ''
  });

  // Função para gerar ID aleatório
  const gerarId = () => {
    const timestamp = new Date().getTime();
    const randomPart = Math.floor(1000 + Math.random() * 9000);
    return `${timestamp}${randomPart}`;
  };

  // Função auxiliar para processar resposta de pedreiros
  const processarRespostaPedreiros = (response) => {
    if (response.success && response.data) {
      if (response.data.pedreiros) {
        return response.data.pedreiros;
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
    }
    return [];
  };

  // Função auxiliar para processar resposta de diárias
  const processarRespostaDiarias = (response) => {
    if (response.success && response.data) {
      if (response.data.diarias) {
        return response.data.diarias;
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
    }
    return [];
  };

  // Buscar dados do usuário
  useEffect(() => {
    const buscarDados = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Buscar pedreiros
        const responsePedreiros = await apiService.getPedreiros();
        setPedreiros(processarRespostaPedreiros(responsePedreiros));
        
        // Buscar diárias
        const responseDiarias = await apiService.getDiarias();
        setDiarias(processarRespostaDiarias(responseDiarias));
        
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        toast.error('Erro ao carregar dados. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    buscarDados();
  }, [currentUser]);

  // Calcular saldo devedor por pedreiro
  const calcularSaldoDevedor = (pedreiroId) => {
    const diariasPedreiro = diarias.filter(d => d.pedreiroId === pedreiroId);
    const totalDiarias = diariasPedreiro.reduce((total, d) => {
      return total + (parseFloat(d.valorUnitario) * parseInt(d.quantidade));
    }, 0);
    
    // Buscar pagamentos já realizados (quitações)
    const pagamentos = diarias.filter(d => 
      d.pedreiroId === pedreiroId && d.tipo === 'pagamento'
    );
    const totalPagamentos = pagamentos.reduce((total, p) => {
      return total + parseFloat(p.valorUnitario);
    }, 0);
    
    return totalDiarias - totalPagamentos;
  };

  // Abrir modal para adicionar pedreiro
  const abrirModalPedreiro = () => {
    setModalTipo('pedreiro');
    setEditandoId(null);
    setFormPedreiro({
      nome: '',
      valorDiaria: '',
      telefone: '',
      observacoes: ''
    });
    setModalAberta(true);
  };

  // Abrir modal para adicionar diária
  const abrirModalDiaria = () => {
    setModalTipo('diaria');
    setEditandoId(null);
    setFormDiaria({
      pedreiroId: '',
      data: new Date().toISOString().substring(0, 10),
      quantidade: 1,
      valorUnitario: '',
      observacoes: ''
    });
    setModalAberta(true);
  };

  // Abrir modal para quitar saldo
  const abrirModalQuitarSaldo = (pedreiro) => {
    setModalTipo('quitar');
    setEditandoId(pedreiro.id);
    const saldoDevedor = calcularSaldoDevedor(pedreiro.id);
    setFormDiaria({
      pedreiroId: pedreiro.id,
      data: new Date().toISOString().substring(0, 10),
      quantidade: 1,
      valorUnitario: saldoDevedor.toString(),
      observacoes: 'Quitação de saldo devedor'
    });
    setModalAberta(true);
  };

  // Editar pedreiro
  const editarPedreiro = (pedreiro) => {
    setModalTipo('pedreiro');
    setEditandoId(pedreiro.id);
    setFormPedreiro({
      nome: pedreiro.nome,
      valorDiaria: pedreiro.valorDiaria.toString(),
      telefone: pedreiro.telefone || '',
      observacoes: pedreiro.observacoes || ''
    });
    setModalAberta(true);
  };

  // Manipular mudanças no formulário de pedreiro
  const handlePedreiroChange = (e) => {
    const { name, value } = e.target;
    setFormPedreiro(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manipular mudanças no formulário de diária
  const handleDiariaChange = (e) => {
    const { name, value } = e.target;
    setFormDiaria(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Salvar pedreiro
  const salvarPedreiro = async (e) => {
    e.preventDefault();
    
    if (!formPedreiro.nome.trim() || !formPedreiro.valorDiaria.trim()) {
      toast.error('Nome e valor da diária são obrigatórios.');
      return;
    }

    try {
      const dadosPedreiro = {
        ...formPedreiro,
        valorDiaria: parseFloat(formPedreiro.valorDiaria),
        userId: currentUser.uid
      };

      let response;
      if (editandoId) {
        // Editar pedreiro existente
        response = await apiService.updatePedreiro(editandoId, dadosPedreiro);
      } else {
        // Adicionar novo pedreiro
        dadosPedreiro.id = gerarId();
        response = await apiService.addPedreiro(dadosPedreiro);
      }

      if (response.success) {
        toast.success(editandoId ? 'Pedreiro atualizado com sucesso!' : 'Pedreiro adicionado com sucesso!');
        setModalAberta(false);
        
        // Recarregar lista de pedreiros
        const responsePedreiros = await apiService.getPedreiros();
        setPedreiros(processarRespostaPedreiros(responsePedreiros));
      } else {
        toast.error(response.message || 'Erro ao salvar pedreiro.');
      }
    } catch (error) {
      console.error('Erro ao salvar pedreiro:', error);
      toast.error('Erro ao salvar pedreiro. Tente novamente.');
    }
  };

  // Salvar diária
  const salvarDiaria = async (e) => {
    e.preventDefault();
    
    if (!formDiaria.pedreiroId || !formDiaria.valorUnitario.trim()) {
      toast.error('Pedreiro e valor são obrigatórios.');
      return;
    }

    try {
      const dadosDiaria = {
        ...formDiaria,
        valorUnitario: parseFloat(formDiaria.valorUnitario),
        quantidade: parseInt(formDiaria.quantidade),
        userId: currentUser.uid,
        tipo: modalTipo === 'quitar' ? 'pagamento' : 'diaria'
      };

      dadosDiaria.id = gerarId();
      const response = await apiService.addDiaria(dadosDiaria);

      if (response.success) {
        toast.success(modalTipo === 'quitar' ? 'Saldo quitado com sucesso!' : 'Diária registrada com sucesso!');
        setModalAberta(false);
        
        // Se for quitação, adicionar ao controle de custos
        if (modalTipo === 'quitar') {
          await adicionarAoControleCustos(dadosDiaria);
        }
        
        // Recarregar lista de diárias
        const responseDiarias = await apiService.getDiarias();
        setDiarias(processarRespostaDiarias(responseDiarias));
      } else {
        toast.error(response.message || 'Erro ao salvar diária.');
      }
    } catch (error) {
      console.error('Erro ao salvar diária:', error);
      toast.error('Erro ao salvar diária. Tente novamente.');
    }
  };

  // Adicionar quitação ao controle de custos
  const adicionarAoControleCustos = async (dadosDiaria) => {
    try {
      const pedreiro = pedreiros.find(p => p.id === dadosDiaria.pedreiroId);
      const transacao = {
        id: gerarId(),
        tipo: 'saida',
        descricao: `Quitação de saldo - ${pedreiro?.nome || 'Pedreiro'}`,
        valor: dadosDiaria.valorUnitario.toString(),
        categoria: 'diaria',
        data: dadosDiaria.data,
        formaPagamento: 'dinheiro',
        entidadeNome: pedreiro?.nome || 'Pedreiro',
        entidadeTipo: 'funcionario',
        userId: currentUser.uid,
        observacoes: dadosDiaria.observacoes
      };

      const response = await apiService.addCusto(transacao);
      if (response.success) {
        toast.success('Transação adicionada ao controle de custos.');
      }
    } catch (error) {
      console.error('Erro ao adicionar ao controle de custos:', error);
      toast.error('Erro ao adicionar ao controle de custos.');
    }
  };

  // Excluir pedreiro
  const excluirPedreiro = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedreiro?')) {
      return;
    }

    try {
      const response = await apiService.deletePedreiro(id);
      if (response.success) {
        toast.success('Pedreiro excluído com sucesso!');
        
        // Recarregar lista de pedreiros
        const responsePedreiros = await apiService.getPedreiros();
        setPedreiros(processarRespostaPedreiros(responsePedreiros));
      } else {
        toast.error(response.message || 'Erro ao excluir pedreiro.');
      }
    } catch (error) {
      console.error('Erro ao excluir pedreiro:', error);
      toast.error('Erro ao excluir pedreiro. Tente novamente.');
    }
  };

  // Excluir diária
  const excluirDiaria = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta diária?')) {
      return;
    }

    try {
      const response = await apiService.deleteDiaria(id);
      if (response.success) {
        toast.success('Diária excluída com sucesso!');
        
        // Recarregar lista de diárias
        const responseDiarias = await apiService.getDiarias();
        setDiarias(processarRespostaDiarias(responseDiarias));
      } else {
        toast.error(response.message || 'Erro ao excluir diária.');
      }
    } catch (error) {
      console.error('Erro ao excluir diária:', error);
      toast.error('Erro ao excluir diária. Tente novamente.');
    }
  };

  // Fechar modal
  const fecharModal = () => {
    setModalAberta(false);
    setEditandoId(null);
  };

  // Formatar data
  const formatarData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  // Formatar valor
  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Navegação
  const handleNavigateToHome = () => {
    navigate('/home');
  };

  const handleNavigateToOrcamentos = () => {
    navigate('/orcamentos');
  };

  const handleNavigateToDiarioObra = () => {
    navigate('/diario-obra');
  };

  const handleNavigateToControleCustos = () => {
    navigate('/controle-custos');
  };

  if (loading) {
    return (
      <div className="controle-diarias-container" data-theme={darkMode ? 'dark' : 'light'}>
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="controle-diarias-container" data-theme={darkMode ? 'dark' : 'light'}>
      <Header 
        onNavigateToHome={handleNavigateToHome}
        onNavigateToOrcamentos={handleNavigateToOrcamentos}
        onNavigateToDiarioObra={handleNavigateToDiarioObra}
      />
      
      <div className="controle-diarias-content">
        <div className="controle-diarias-header">
          <h1>Controle de Diárias de Pedreiros</h1>
          <div className="controle-diarias-actions">
            <button 
              className="btn btn-primary"
              onClick={abrirModalPedreiro}
            >
              <i className="fas fa-user-plus"></i>
              Adicionar Pedreiro
            </button>
            <button 
              className="btn btn-success"
              onClick={abrirModalDiaria}
              disabled={pedreiros.length === 0}
            >
              <i className="fas fa-plus"></i>
              Registrar Diária
            </button>
          </div>
        </div>

        <div className="controle-diarias-grid">
          {/* Lista de Pedreiros */}
          <div className="pedreiros-section">
            <h2>Pedreiros Cadastrados</h2>
            <div className="pedreiros-list">
              {pedreiros.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-users"></i>
                  <p>Nenhum pedreiro cadastrado</p>
                  <button 
                    className="btn btn-primary"
                    onClick={abrirModalPedreiro}
                  >
                    Adicionar Primeiro Pedreiro
                  </button>
                </div>
              ) : (
                pedreiros.map(pedreiro => {
                  const saldoDevedor = calcularSaldoDevedor(pedreiro.id);
                  return (
                    <div key={pedreiro.id} className="pedreiro-card">
                      <div className="pedreiro-info">
                        <h3>{pedreiro.nome}</h3>
                        <p><strong>Diária:</strong> {formatarValor(pedreiro.valorDiaria)}</p>
                        {pedreiro.telefone && (
                          <p><strong>Telefone:</strong> {pedreiro.telefone}</p>
                        )}
                        <p><strong>Saldo Devedor:</strong> 
                          <span className={saldoDevedor > 0 ? 'saldo-devedor' : 'saldo-zerado'}>
                            {formatarValor(saldoDevedor)}
                          </span>
                        </p>
                      </div>
                      <div className="pedreiro-actions">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => editarPedreiro(pedreiro)}
                          title="Editar pedreiro"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => abrirModalQuitarSaldo(pedreiro)}
                          disabled={saldoDevedor <= 0}
                          title="Quitar saldo"
                        >
                          <i className="fas fa-dollar-sign"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => excluirPedreiro(pedreiro.id)}
                          title="Excluir pedreiro"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Histórico de Diárias */}
          <div className="diarias-section">
            <h2>Histórico de Diárias</h2>
            <div className="diarias-list">
              {diarias.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-calendar-alt"></i>
                  <p>Nenhuma diária registrada</p>
                </div>
              ) : (
                <div className="diarias-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Pedreiro</th>
                        <th>Tipo</th>
                        <th>Qtd</th>
                        <th>Valor Unit.</th>
                        <th>Total</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diarias
                        .sort((a, b) => new Date(b.data) - new Date(a.data))
                        .map(diaria => {
                          const pedreiro = pedreiros.find(p => p.id === diaria.pedreiroId);
                          const total = parseFloat(diaria.valorUnitario) * parseInt(diaria.quantidade);
                          return (
                            <tr key={diaria.id}>
                              <td>{formatarData(diaria.data)}</td>
                              <td>{pedreiro?.nome || 'N/A'}</td>
                              <td>
                                <span className={`tipo-badge ${diaria.tipo}`}>
                                  {diaria.tipo === 'pagamento' ? 'Pagamento' : 'Diária'}
                                </span>
                              </td>
                              <td>{diaria.quantidade}</td>
                              <td>{formatarValor(diaria.valorUnitario)}</td>
                              <td>{formatarValor(total)}</td>
                              <td>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => excluirDiaria(diaria.id)}
                                  title="Excluir diária"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalAberta && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalTipo === 'pedreiro' && (editandoId ? 'Editar Pedreiro' : 'Adicionar Pedreiro')}
                {modalTipo === 'diaria' && 'Registrar Diária'}
                {modalTipo === 'quitar' && 'Quitar Saldo Devedor'}
              </h3>
              <button className="modal-close" onClick={fecharModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={modalTipo === 'pedreiro' ? salvarPedreiro : salvarDiaria}>
              {modalTipo === 'pedreiro' ? (
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="nome">Nome do Pedreiro *</label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formPedreiro.nome}
                      onChange={handlePedreiroChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="valorDiaria">Valor da Diária (R$) *</label>
                    <input
                      type="number"
                      id="valorDiaria"
                      name="valorDiaria"
                      value={formPedreiro.valorDiaria}
                      onChange={handlePedreiroChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="telefone">Telefone</label>
                    <input
                      type="text"
                      id="telefone"
                      name="telefone"
                      value={formPedreiro.telefone}
                      onChange={handlePedreiroChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="observacoes">Observações</label>
                    <textarea
                      id="observacoes"
                      name="observacoes"
                      value={formPedreiro.observacoes}
                      onChange={handlePedreiroChange}
                      rows="3"
                    />
                  </div>
                </div>
              ) : (
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="pedreiroId">Pedreiro *</label>
                    <select
                      id="pedreiroId"
                      name="pedreiroId"
                      value={formDiaria.pedreiroId}
                      onChange={handleDiariaChange}
                      required
                      disabled={modalTipo === 'quitar'}
                    >
                      <option value="">Selecione um pedreiro</option>
                      {pedreiros.map(pedreiro => (
                        <option key={pedreiro.id} value={pedreiro.id}>
                          {pedreiro.nome} - {formatarValor(pedreiro.valorDiaria)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="data">Data *</label>
                    <input
                      type="date"
                      id="data"
                      name="data"
                      value={formDiaria.data}
                      onChange={handleDiariaChange}
                      required
                    />
                  </div>
                  
                  {modalTipo !== 'quitar' && (
                    <div className="form-group">
                      <label htmlFor="quantidade">Quantidade de Diárias *</label>
                      <input
                        type="number"
                        id="quantidade"
                        name="quantidade"
                        value={formDiaria.quantidade}
                        onChange={handleDiariaChange}
                        min="1"
                        required
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="valorUnitario">
                      {modalTipo === 'quitar' ? 'Valor do Pagamento (R$) *' : 'Valor Unitário (R$) *'}
                    </label>
                    <input
                      type="number"
                      id="valorUnitario"
                      name="valorUnitario"
                      value={formDiaria.valorUnitario}
                      onChange={handleDiariaChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="observacoes">Observações</label>
                    <textarea
                      id="observacoes"
                      name="observacoes"
                      value={formDiaria.observacoes}
                      onChange={handleDiariaChange}
                      rows="3"
                    />
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={fecharModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalTipo === 'pedreiro' ? (editandoId ? 'Atualizar' : 'Adicionar') : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControleDiarias; 